import { i18n } from 'boot/i18n'
import get from 'lodash/get'
import { copyToClipboard, Dialog, format, Notify, scroll } from 'quasar'

const { getScrollTarget, setVerticalScrollPosition } = scroll

const { capitalize } = format

/**
 * Get the correct API base URL
 */
export const getAPIBaseURL = () => {
  return ['client.nocht-en-wille.nl', 'client-staging.nocht-en-wille.nl'].includes(window.location.hostname)
    ? process.env.API_HOSTNAME_CUSTOMER
    : process.env.API_HOSTNAME
}

export const getPDFUrl = (id, download, token) => {
  return getAPIBaseURL() + '/files/' + id + '?token=' + token + (download ? '&download=1' : '')
}

/**
 * Convert an object schema to empty form data.
 * Optional: Pass the present object data to replace the default values.
 * NOTE: When the present data contains a null value for a nested object, the default data using the schema is returned
 *
 * @param schema - Default object schema
 * @param data - Present data (e.g. returned from the API)
 */
export const schemaToForm = (schema, data = {}) => {
  let obj = {}
  if (data.id) {
    obj.id = data.id
  }

  for (let [key] of Object.entries(schema)) {
    obj[key] = typeof data[key] !== 'undefined' && data[key] !== null ? data[key] : (schema[key].value ?? null)
  }

  return obj
}

export const generateFormErrorsModel = (err, service) => {
  let errorObject = {}
  err.errors?.forEach((error) => {
    if (!error.attr) {
      return
    }

    let attr = error.attr.split('.').pop()

    let key = capitalize(service) + '.' + capitalize(attr)
    let code = error.code
    let detail = error.detail

    if (!errorObject[error.attr]) {
      errorObject[error.attr] = []
    }
    if (!errorObject[error.attr].includes(code)) {
      let message = null
      if (service && i18n.global.te(capitalize(service) + '.Errors.' + capitalize(code))) {
        message = i18n.global.t(capitalize(service) + '.Errors.' + capitalize(code), { key, ...error })
      } else if (i18n.global.te('Globals.Errors.' + capitalize(code))) {
        message = i18n.global.t('Globals.Errors.' + capitalize(code), { key, ...error })
      } else {
        message = i18n.global.t('Globals.Errors.Default', { key, ...error })
      }

      if (!errorObject[error.attr].includes(message)) {
        errorObject[error.attr].push(message)
      }
    }
  })

  return errorObject
}

export const getOptions = (
  key,
  options,
  itemsService,
  config = {
    field: null,
    valueKey: null,
    labelKey: null,
    translateKeyBase: null,
    translateKey: null,
    avatar: false,
    avatarValuePrefix: null,
    avatarValue: null,
  },
  preserveOriginalValues = false,
  translateOptions = true,
) => {
  if (!Array.isArray(options)) {
    return []
  }

  let arr = []
  for (const option of options) {
    let stringOption = ''
    let message = ''
    stringOption = typeof option === 'boolean' ? (option ? 'yes' : 'no') : option
    if (typeof option === 'object' && Object.keys(config).every((option) => config[option])) {
      message = translateOptions ? config.translateKeyBase + '.' + capitalize(stringOption[config.translateKey]) : stringOption[config.translateKey]
      let optionObj = {
        value: option[config.valueKey],
        label: i18n.global.t(message),
      }
      if (config.avatar) {
        optionObj.avatar = (config.avatarValuePrefix ? config.avatarValuePrefix : '') + option[config.avatarValue].toLowerCase()
      }
      if (preserveOriginalValues) {
        optionObj.originalValue = option
      }
      arr.push(optionObj)
    } else if (typeof option === 'object' && !Object.keys(config).every((option) => config[option])) {
      arr.push(option)
    } else {
      message =
        translateOptions && key && itemsService && typeof option !== 'boolean'
          ? capitalize(itemsService) + '.' + capitalize(key) + 'Options.' + capitalize(stringOption)
          : capitalize(stringOption)
      arr.push({
        value: option,
        label: translateOptions ? i18n.global.t(message) : message,
      })
    }
  }
  return arr
}

export const searchValueInRow = (row, searchValue = '', excludedKeys = []) => {
  const lowerSearch = searchValue ? searchValue.toLowerCase() : ''
  const searchableArray = Array.isArray(row) ? row : Object.keys(row)

  return searchableArray.some((key) => {
    let found = false
    if (!excludedKeys.includes(key)) {
      if (row[key] && typeof row[key] === 'object') {
        found = searchValueInRow(row[key], searchValue, excludedKeys)
      } else if (row[key] && typeof row[key] === 'string') {
        found = row[key].toLowerCase().includes(lowerSearch)
      } else if (row[key] && typeof row[key] === 'number') {
        found = row[key].toString().includes(lowerSearch)
      } else if (typeof key === 'string') {
        found = key.toLowerCase().includes(lowerSearch)
      }
    }
    return found
  })
}

export const onError = (payload, itemsService) => {
  let translateString = ''
  if (payload.error?.code && i18n.global.te(capitalize(itemsService) + '.Errors.' + payload.error.code)) {
    translateString = capitalize(itemsService) + '.Errors.' + payload.error.code
  } else if (i18n.global.te(capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail')) {
    translateString = capitalize(itemsService) + '.' + format.capitalize(payload.type) + ' item fail'
  } else if (i18n.global.te(capitalize(itemsService) + '.Errors.default')) {
    translateString = capitalize(itemsService) + '.Errors.default'
  } else {
    translateString = 'Globals.Errors.Item fail'
  }

  const messageData = {
    msg: payload.errors?.showErrorMessage === true ? capitalize(payload.message) : null,
  }

  if (payload.error) {
    Object.assign(messageData, payload.error)
  }

  Notify.create({
    type: 'negative',
    message: i18n.global.t(translateString, messageData),
    progress: true,
    html: true,
  })
}

export const onSuccess = (payload, itemsService, type = '', itemName = null, objectName) => {
  let message

  if (i18n.global.te(capitalize(itemsService) + '.' + capitalize(type) + ' item success')) {
    message = i18n.global.t(capitalize(itemsService) + '.' + capitalize(type) + ' item success', {
      object: objectName,
      item: itemName ? itemName : payload?.fullName ? payload.fullName : payload?.name,
    })
  } else {
    message = i18n.global.t('Globals.' + capitalize(type) + ' item success', {
      object: objectName,
      item: itemName ? itemName : payload?.fullName ? payload.fullName : payload?.name,
    })
  }

  Notify.create({
    type: 'positive',
    message: message,
    progress: true,
    html: true,
  })
}

export const onUpdatedContent = () => {
  Notify.create({
    progress: true,
    timeout: 0,
    color: 'primary',
    icon: 'sym_o_system_update_alt',
    iconSize: 'lg',
    classes: 'q-pa-md',
    actions: [
      {
        iconRight: 'sym_o_sync',
        label: i18n.global.t('Globals.Reload app'),
        class: 'q-btn--outline',
        color: 'white',
        handler: () => {
          window.location.reload(true)
        },
      },
    ],
    message: i18n.global.t('Globals.Update available'),
    html: true,
  })
}

export const openDialogForm = (
  options = {
    type: 'new',
    component: null,
    item: null,
    itemsService: null,
    onOk: null,
    onCancel: null,
    onError: null,
    onSuccess: null,
    onSuccessData: null,
    onDismiss: null,
    customProps: null,
  },
) => {
  Dialog.create({
    component: options.component,
    parent: this,
    componentProps: {
      type: options.type ?? undefined,
      item: options.item ?? undefined,
      itemName: options.itemName ?? undefined,
      itemsService: options.itemsService ?? undefined,
      onError: options.onError ? options.onError : onError,
      onSuccess: options.onSuccess ? options.onSuccess : onSuccess,
      onSuccessData: options.onSuccessData ?? undefined,
      customProps: options.customProps ?? undefined,
    },
  })
    .onOk(async (payload) => {
      if (options.onOk) {
        options.onOk(payload)
      }
    })
    .onCancel((payload) => {
      if (options.onCancel) {
        options.onCancel(payload)
      }
    })
    .onDismiss((payload) => {
      if (options.onDismiss) {
        options.onDismiss(payload)
      }
    })
}

/**
 * Converts a camelCase string into a space‑separated string
 * with each word’s first letter capitalized.
 *
 * @param {string} string - The camelCase string to convert
 * @returns {string} A space‑separated, capitalized version of the input
 *
 * @example
 * // simple conversion
 * console.log(camelToFlat('helloWorld')) // 'Hello World'
 *
 * @example
 * // multiple words
 * console.log(camelToFlat('thisIsATestString')) // 'This Is A Test String'
 *
 * @example
 * // edge cases: empty or single word
 * console.log(camelToFlat('')) // ''
 * console.log(camelToFlat('single')) // 'Single'
 */
export const camelToFlat = (string) => {
  const camelCase = string.replace(/([a-z])([A-Z])/g, '$1 $2').split(' ')

  let flat = ''

  camelCase.forEach((word) => {
    flat = flat + word.charAt(0).toUpperCase() + word.slice(1) + ' '
  })
  return flat.trim()
}

/**
 * Sort an Array with Objects based on the provided field
 *
 * @param {Array} data - Array with Objects
 * @param {String|Array} fields - Field to be used for sorting
 * @param {Boolean} [ascending=true] - Sort ascending?
 * @returns {Array} - Sorted array
 */
export const sortObjectBy = (data, fields, ascending = true) => {
  // If data was not (yet) loaded or is undefined/null, return an empty array immediately
  if (typeof data === 'undefined' || data === null) {
    return []
  }

  if (typeof fields === 'string') {
    fields = [fields]
  }
  let returnArray = data
  for (const field of fields) {
    returnArray = returnArray.sort((a, b) => {
      const aField = get(a, field)
      const bField = get(b, field)
      const nameA = typeof aField === 'string' ? aField.toUpperCase() : aField // ignore upper and lowercase
      const nameB = typeof bField === 'string' ? bField.toUpperCase() : bField // ignore upper and lowercase
      if (nameA < nameB) {
        return ascending ? -1 : 1
      }
      if (nameA > nameB) {
        return ascending ? 1 : -1
      }

      // names must be equal
      return 0
    })
  }
  return returnArray
}

/**
 * Convert number to localized value
 * @param {string} lang                           - ISO language code to use for localization (e.g. en-US)
 * @param {number} number                         - Number to convert
 * @param {boolean} [showCurrencySymbol=false]    - Convert number to currency?
 * @param {string} [type='currency']              - Style for formatting. See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options|MDN Webdocs} for options. NOTE: Is omitted when showCurrencySymbol is set to true
 * @param {object} [options={}]                   - Additional options for formatting. See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#style_options|MDN Webdocs} for options. NOTE: Is omitted when showCurrencySymbol is set to true
 * @returns {string}                              - Formatted localized string
 */
export const numberFormat = (lang, number, showCurrencySymbol = false, type = 'currency', options = {}) => {
  if (typeof options.minimumFractionDigits === 'undefined') {
    let minDigits = 2
    if (type === 'percentage') {
      minDigits = 0
    }
    options = {
      ...options,
      minimumFractionDigits: minDigits,
    }
  }

  if (typeof options.maximumFractionDigits === 'undefined') {
    options = {
      ...options,
      maximumFractionDigits: 2,
    }
  }

  if (showCurrencySymbol) {
    options.style = 'currency'
    options.currency = 'EUR'
  }

  return new Intl.NumberFormat(lang, options).format(number)
}

export const itemIsObjectNotArray = (data) => {
  return typeof data === 'object' && !Array.isArray(data)
}

/**
 * Adds all calculations together for (selected) customers
 *
 * @param {Object} source - Object with data to add
 * @param {Object} destination - Object to which the source should be added
 * @returns {Object} - Objects given as destination param, but with source data added to it
 */
export const addToTarget = (source, destination) => {
  let obj = { ...destination }
  for (const item of Object.keys(source)) {
    if (item.toLowerCase() === 'error') {
      continue
    }
    if (itemIsObjectNotArray(source[item])) {
      if (typeof obj[item] === 'undefined') {
        obj[item] = {}
      }
      obj[item] = { ...obj[item], ...addToTarget(source[item], obj[item]) }
    } else {
      if (typeof obj[item] === 'undefined') {
        obj[item] = Array.isArray(source[item]) ? [] : 0
      }
      if (Array.isArray(source[item])) {
        obj[item] = obj[item].concat(source[item])
      } else {
        obj[item] += source[item]
      }
    }
  }
  return obj
}

export const createChartDataSet = (data = {}, fields = [], parentKey = null, alternateLabel = null, alternateLabelType = 'string', stripFromLabel = '') => {
  let labels = []
  let chartData = []
  let colorCount = 0

  let itemData = {
    data: [],
    backgroundColor: [],
  }
  if (itemIsObjectNotArray(data)) {
    const colors = randomRGBAArray(Object.keys(data).length)
    for (let key of Object.keys(data)) {
      const strippedKey = alternateLabel
        ? alternateLabelType === 'string'
          ? camelToFlat(alternateLabel.replaceAll(stripFromLabel, ''))
          : data[alternateLabel].replaceAll(stripFromLabel, '')
        : camelToFlat(key.replaceAll(stripFromLabel, ''))
      if (fields.length === 0 || fields.includes(key)) {
        labels.push(strippedKey)
        if (parentKey) {
          itemData.label = camelToFlat(parentKey)
        }
        itemData.data.push({ key: strippedKey, value: data[key] })
        itemData.backgroundColor.push(colors[colorCount])
        colorCount++
      }
    }
  } else {
    for (const item of data) {
      const colors = randomRGBAArray(data.length)
      for (let key of Object.keys(item)) {
        const strippedKey = alternateLabel
          ? alternateLabelType === 'string'
            ? camelToFlat(alternateLabel.replaceAll(stripFromLabel, ''))
            : item[alternateLabel].replaceAll(stripFromLabel, '')
          : camelToFlat(key.replaceAll(stripFromLabel, ''))
        if (fields.length === 0 || fields.includes(key)) {
          labels.push(strippedKey)
          if (parentKey) {
            itemData.label = camelToFlat(parentKey)
          }
          itemData.data.push({ key: strippedKey, value: item[key] })
          itemData.backgroundColor.push(colors[colorCount])
        }
      }
      colorCount++
    }
  }
  chartData.push(itemData)
  chartData.forEach((set) => {
    sortObjectBy(set.data, 'key')
  })
  return {
    labels: labels.sort(),
    datasets: chartData,
  }
}

/**
 * Adds all calculations together for (selected) customers to add into a line chart
 * @param {Object} data - Object with data to add
 * @param {string} keyPath - String with JSPath
 * @param {Array} excludedKeys - Array with keys that should be excluded from the chart
 * @param {boolean} sortLegend - Should the legend be sorted
 * @param {boolean} [round=false] - Should the values be rounded
 * @param {number} [digits=0] - If the value should be rounded, how many decimals
 * @param {boolean} [addMissingData=true] - Fill missing data with 0 values?
 * @returns {Object} - Data object with datasets and labels
 */
export const createChartDataSetHistory = (data, keyPath, excludedKeys = [], sortLegend = true, round = false, digits = 0, addMissingData = true) => {
  const chartDataMap = new Map()
  const dateRange = Object.keys(data).sort((a, b) => {
    return new Date(a) - new Date(b)
  })

  const labelArray = []

  for (const dateData of Object.values(data)) {
    const getPath = get(dateData, keyPath)

    if (!getPath) {
      continue
    }

    for (const key of Object.keys(getPath)) {
      if (excludedKeys.includes(key)) {
        continue
      }
      if (!labelArray.includes(camelToFlat(key))) {
        labelArray.push(camelToFlat(key))
      }
    }
  }

  const colors = randomRGBAArray(labelArray.length)
  let colorCount = 0
  for (const date of dateRange) {
    const requestedChartData = get(data[date], keyPath)

    if (!requestedChartData) {
      continue
    }

    const chartDataEntries = Object.entries(requestedChartData)
    const labelArrayLastRun = []

    for (const [key, value] of chartDataEntries) {
      if (excludedKeys.includes(key)) {
        continue
      }
      const formattedKey = camelToFlat(key)
      let dataset = chartDataMap.get(formattedKey)

      if (!dataset) {
        dataset = {
          label: formattedKey,
          data: [],
          backgroundColor: colors[colorCount],
          borderColor: colors[colorCount],
        }

        colorCount++
        chartDataMap.set(formattedKey, dataset)
      }

      if (!labelArrayLastRun.includes(formattedKey)) {
        labelArrayLastRun.push(formattedKey)
      }

      dataset.data.push({ x: date, y: round ? Number(value).toFixed(digits) : value })
    }

    if (addMissingData) {
      const missingKeys = labelArray.filter((label) => !labelArrayLastRun.includes(label))

      for (const missingKey of missingKeys) {
        const formattedMissingKeys = camelToFlat(missingKey)
        let dataset = chartDataMap.get(formattedMissingKeys)
        if (!dataset) {
          dataset = {
            label: formattedMissingKeys,
            data: [],
            backgroundColor: colors[colorCount],
            borderColor: colors[colorCount],
          }

          colorCount++
          chartDataMap.set(formattedMissingKeys, dataset)
        }

        dataset.data.push({ x: date, y: round ? Number(0).toFixed(digits) : 0 })
      }
    }
  }

  return {
    datasets: sortLegend ? sortObjectBy(Array.from(chartDataMap.values()), 'label') : Array.from(chartDataMap.values()),
  }
}

export const hsvToRgb = (h, s, v) => {
  const h_i = Math.floor(h * 6)
  const f = h * 6 - h_i
  const p = v * (1 - s)
  const q = v * (1 - f * s)
  const t = v * (1 - (1 - f) * s)
  let r, g, b

  switch (h_i) {
    case 0:
      r = v
      g = t
      b = p
      break
    case 1:
      r = q
      g = v
      b = p
      break
    case 2:
      r = p
      g = v
      b = t
      break
    case 3:
      r = p
      g = q
      b = v
      break
    case 4:
      r = t
      g = p
      b = v
      break
    case 5:
      r = v
      g = p
      b = q
      break
  }

  return [Math.floor(r * 255), Math.floor(g * 255), Math.floor(b * 255)]
}

export const randomRGBAArray = (count = 1) => {
  const colors = []
  const goldenRatioConjugate = 0.618033988749895
  const minSaturation = 0.4
  const maxSaturation = 0.8
  const minValue = 0.6
  const maxValue = 0.9

  for (let i = 0; i < count; i++) {
    const saturation = minSaturation + (maxSaturation - minSaturation) * (i / count)
    const value = minValue + (maxValue - minValue) * (i / count)
    const hue = (i * goldenRatioConjugate) % 1
    const rgb = hsvToRgb(hue, saturation, value)
    colors.push(rgb)
  }

  return colors.map((color) => `rgb(${color.join(',')})`)
}

export const copyText = (text) => {
  copyToClipboard(text).catch((e) => {
    Notify.create({
      type: 'negative',
      message: i18n.global.t('Globals.Copy fail', { error: e }),
    })
  })

  Notify.create({
    type: 'positive',
    icon: 'sym_o_content_copy',
    message: i18n.global.t('Globals.Copy success'),
  })
}

// takes an element object
export const scrollToElement = (selector, duration = 1000) => {
  const element = document.querySelector(selector)
  window.testElement = element
  if (element) {
    const target = getScrollTarget(element)
    const offset = element.offsetTop
    setVerticalScrollPosition(target, offset, duration)
  }
}

export const areArraysEqualUnordered = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false
  }

  const sortedArr1 = [...arr1].sort()
  const sortedArr2 = [...arr2].sort()

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false
    }
  }

  return true
}

/**
 * Compare 2 object for equality
 * @param object1
 * @param object2
 * @returns {boolean}
 */
export const areObjectsEqual = (object1, object2) => {
  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    const val1 = object1[key]
    const val2 = object2[key]
    const areObjects = isObject(val1) && isObject(val2)
    if ((areObjects && !areObjectsEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false
    }
  }

  return true
}

/**
 * Determines if data is an object
 * @param object
 * @returns {boolean}
 */
export const isObject = (object) => {
  return object != null && typeof object === 'object'
}

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export const updateArray = (array, item, remove = false, resort = null) => {
  if (!item) {
    return
  }

  const foundIndex = array.findIndex((arrayItem) => arrayItem._id === item._id)
  if (foundIndex > -1) {
    if (remove) {
      array.splice(foundIndex, 1)
    } else {
      array.splice(foundIndex, 1, item)
    }
  } else {
    array.push(item)
  }
}

/**
 * Moves an item in an Array from an old index to a new index
 * @param arrayToReorder      - Array to move item in
 * @param oldIndex              - Old index
 * @param newIndex              - New index
 * @returns {Array}           - Reordered array
 */
export const reorderArrayBasedOnIndexes = (arrayToReorder, oldIndex, newIndex) => {
  const itemToMove = arrayToReorder.splice(oldIndex, 1)[0]
  arrayToReorder.splice(newIndex, 0, itemToMove)
  return arrayToReorder
}

/**
 * Converts number to Object with hours and minutes
 * @param number
 * @returns {{hours: number, minutes: number}|{hours: (number|number), minutes: number}}
 */
export const hoursToHuman = (number) => {
  if (!number || isNaN(number)) {
    return { hours: 0, minutes: 0 }
  }

  let hours = Math.floor(number)
  let minutes = Math.round((number - hours) * 60)

  // Round minutes to the nearest 5-minute interval
  minutes = Math.ceil(minutes / 5) * 5

  // Adjust hours if minutes exceeds 59
  if (minutes >= 60) {
    hours++
    minutes -= 60
  }

  return { hours, minutes }
}

export const getQueryParams = (url) => {
  const params = {}
  const urlObj = new URL(url)
  urlObj.searchParams.forEach((value, key) => {
    params[key] = value
  })
  return params
}

export const getQueryParam = (url, param) => {
  const params = getQueryParams(url)
  return params[param]
}

export const getImageMeta = (url, cb) => {
  const img = new Image()
  img.onload = () => cb(null, img)
  img.onerror = (err) => cb(err)
  img.src = url
}

export const downloadBlob = async (accessToken, href, filename, download = false) => {
  try {
    // Check if we are on Cordova Android
    if (isCordovaAndroid()) {
      return await downloadBlobCordovaAndroid(accessToken, href, filename, download)
    } else {
      return await downloadBlobBrowser(accessToken, href, filename, download)
    }
  } catch (error) {
    // Fallback error handling (only triggered if something throws in the helpers)
    Notify.create({
      type: 'negative',
      message: i18n.global.t('Globals.Download fail'),
      progress: true,
      html: true,
    })
    throw error
  }
}

/* ============================================
   1. Download in a **web browser** environment
   ============================================ */
export const downloadBlobBrowser = async (accessToken, href, filename, download = false) => {
  const response = await fetch(href, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`)
      }
      return response
    })
    .catch((err) => {
      throw err
    })

  const blob = await response.blob()
  const url = window.URL.createObjectURL(blob)

  if (download) {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)

    // Notify success in your existing UI framework
    Notify.create({
      type: 'positive',
      message: i18n.global.t('Globals.Download success'),
      progress: true,
      html: true,
    })
  } else {
    // Return the blob URL if user wants to open or handle it in some other way
    return url
  }
}

/* ============================================
   2. Download in **Cordova Android** environment
   ============================================ */
export const downloadBlobCordovaAndroid = async (accessToken, href, filename, download = false) => {
  // This function is not async (on the signature),
  // but it returns a Promise, so `await` in the caller works fine.
  return new Promise(async (resolve, reject) => {
    try {
      const response = await fetch(href, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).catch((err) => {
        reject(err)
      })
      const blob = await response.blob()

      if (download) {
        const targetDirectory = cordova.file.externalRootDirectory + 'Download/'

        window.resolveLocalFileSystemURL(
          targetDirectory,
          (dirEntry) => {
            // We cannot mark this callback "async", so we use an IIFE inside:
            ;(async () => {
              try {
                // Get a unique filename (no overwrites)
                const uniqueName = await getUniqueFileName(dirEntry, filename)

                dirEntry.getFile(
                  uniqueName,
                  { create: true, exclusive: false },
                  (fileEntry) => {
                    fileEntry.createWriter((fileWriter) => {
                      fileWriter.onwriteend = () => {
                        // Notify success
                        Notify.create({
                          type: 'positive',
                          message: i18n.global.t('Globals.Download success'),
                          progress: true,
                          html: true,
                        })
                        resolve(uniqueName)
                      }

                      fileWriter.onerror = (err) => {
                        Notify.create({
                          type: 'negative',
                          message: i18n.global.t('Globals.Download fail'),
                          progress: true,
                          html: true,
                        })
                        reject(err)
                      }

                      // Write the blob to the file
                      fileWriter.write(blob)
                    }, reject)
                  },
                  reject,
                )
              } catch (innerErr) {
                reject(innerErr)
              }
            })()
          },
          (err) => {
            Notify.create({
              type: 'negative',
              message: i18n.global.t('Globals.Download fail'),
              progress: true,
              html: true,
            })
            reject(err)
          },
        )
      } else {
        resolve(window.URL.createObjectURL(blob))
      }
    } catch (outerErr) {
      // Network errors, fetch() errors, etc.
      Notify.create({
        type: 'negative',
        message: i18n.global.t('Globals.Download fail'),
        progress: true,
        html: true,
      })
      reject(outerErr)
    }
  })
}

/* ============================================
   3. Helper: Detect if running on Cordova Android
   ============================================ */
export const isCordovaAndroid = () => {
  return typeof cordova !== 'undefined' && cordova?.platformId === 'android'
}

const CORDOVA_BROWSER_THEME = {
  toolbarcolor: '#3b0052',
  navigationbuttoncolor: '#ffffff',
  closebuttoncolor: '#ffffff',
}

const DEFAULT_INTERNAL_FRONTEND_ORIGIN = 'http://localhost:9200'
const DEFAULT_EXTERNAL_FRONTEND_ORIGIN = 'http://localhost:9201'

const toOrigin = (value) => {
  try {
    return typeof value === 'string' && value !== '' ? new URL(value).origin : null
  } catch {
    return null
  }
}

const getCurrentOrigin = () => {
  if (typeof window === 'undefined') {
    return null
  }

  return toOrigin(window.location.href)
}

const getFrontendOriginFromApiHostname = () => {
  let apiUrl

  try {
    apiUrl = new URL(process.env.API_HOSTNAME)
  } catch {
    return null
  }

  const isExternalFrontend = process.env.FRONTEND_TYPE === 'External'

  switch (apiUrl.hostname) {
    case 'client-api.nocht-en-wille.nl':
      return 'https://client.nocht-en-wille.nl'
    case 'client-api-staging.nocht-en-wille.nl':
      return 'https://client-staging.nocht-en-wille.nl'
    case 'ecd-api.nocht-en-wille.nl':
      return isExternalFrontend ? 'https://client.nocht-en-wille.nl' : 'https://ecd.nocht-en-wille.nl'
    case 'ecd-api-staging.nocht-en-wille.nl':
      return isExternalFrontend ? 'https://client-staging.nocht-en-wille.nl' : 'https://ecd-staging.nocht-en-wille.nl'
    default:
      return null
  }
}

const isLocalApiHostname = () => {
  let apiUrl

  try {
    apiUrl = new URL(process.env.API_HOSTNAME)
  } catch {
    return false
  }

  return ['localhost', '127.0.0.1', '10.0.2.2'].includes(apiUrl.hostname) || apiUrl.hostname.startsWith('192.168.')
}

const getFrontendOrigin = () => {
  const originFromApiHostname = getFrontendOriginFromApiHostname()
  if (originFromApiHostname) {
    return originFromApiHostname
  }

  const currentOrigin = getCurrentOrigin()

  if (isLocalApiHostname() && currentOrigin) {
    return currentOrigin
  }

  if (process.env.FRONTEND_TYPE === 'External') {
    return toOrigin(process.env.FRONTEND_HOSTNAME_CUSTOMER) || currentOrigin || DEFAULT_EXTERNAL_FRONTEND_ORIGIN
  }

  return currentOrigin || DEFAULT_INTERNAL_FRONTEND_ORIGIN
}

export const getCordovaInAppBrowserOptions = (overrides = {}) => {
  const options = {
    location: 'yes',
    hideurlbar: 'yes',
    hidenavigationbuttons: 'yes',
    zoom: 'no',
    hardwareback: 'yes',
    fullscreen: 'no',
    lefttoright: 'no',
    ...CORDOVA_BROWSER_THEME,
    ...overrides,
  }

  return Object.entries(options)
    .map(([key, value]) => `${key}=${value}`)
    .join(',')
}

const parseDocsUrl = (path) => {
  return new URL(typeof path === 'string' && path !== '' ? path : '/docs/employee/', getFrontendOrigin())
}

const isDocsPathname = (pathname = '') => pathname === '/docs' || pathname.startsWith('/docs/')

export const normalizeCordovaDocsPath = (path) => {
  const url = parseDocsUrl(path)

  if (url.pathname === '/docs' || url.pathname === '/docs/') {
    url.pathname = '/docs/employee/'
  } else if (url.pathname === '/docs/employee') {
    url.pathname = '/docs/employee/'
  } else if (url.pathname === '/docs/representative') {
    url.pathname = '/docs/employee/'
  } else if (url.pathname.startsWith('/docs/representative/')) {
    url.pathname = url.pathname.replace('/docs/representative/', '/docs/employee/')
  } else if (url.pathname === '/docs/authentication') {
    url.pathname = '/docs/authentication/'
  }

  return `${url.pathname}${url.search}${url.hash}`
}

export const resolveCordovaDocsFilePath = (path) => {
  const url = parseDocsUrl(normalizeCordovaDocsPath(path))

  if (!/\.[a-z0-9]+$/i.test(url.pathname)) {
    url.pathname = url.pathname.endsWith('/') ? `${url.pathname}index.html` : `${url.pathname}/index.html`
  }

  return `${url.pathname}${url.search}${url.hash}`
}

export const isCordovaDocsPath = (path) => {
  const { pathname } = parseDocsUrl(path)

  return (
    pathname === '/docs' ||
    pathname === '/docs/' ||
    pathname === '/docs/employee' ||
    pathname.startsWith('/docs/employee/') ||
    pathname === '/docs/representative' ||
    pathname.startsWith('/docs/representative/') ||
    pathname === '/docs/authentication' ||
    pathname.startsWith('/docs/authentication/')
  )
}

export const openCordovaDocs = (path, options = getCordovaInAppBrowserOptions()) => {
  if (!isCordovaAndroid() || typeof window === 'undefined' || typeof cordova?.InAppBrowser?.open !== 'function') {
    return false
  }

  const targetUrl = new URL(typeof path === 'string' && path !== '' ? path : '/docs/employee/', getFrontendOrigin()).toString()
  const browser = cordova.InAppBrowser.open(targetUrl, '_blank', options)
  const targetOrigin = new URL(targetUrl).origin
  let isClosingForAppNavigation = false

  browser.addEventListener('loadstart', (event) => {
    if (isClosingForAppNavigation || typeof event?.url !== 'string') {
      return
    }

    let nextUrl
    try {
      nextUrl = new URL(event.url)
    } catch {
      return
    }

    if (nextUrl.origin !== targetOrigin || isDocsPathname(nextUrl.pathname)) {
      return
    }

    isClosingForAppNavigation = true
    browser.close()
    window.location.assign(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}` || '/')
  })

  return true
}

/* ============================================
   4. Helper: Generate a unique filename if one already exists
   ============================================ */
const getUniqueFileName = (dirEntry, filename) => {
  return new Promise((resolve, reject) => {
    const dotIndex = filename.lastIndexOf('.')
    const hasExtension = dotIndex !== -1
    const baseName = hasExtension ? filename.substring(0, dotIndex) : filename
    const extension = hasExtension ? filename.substring(dotIndex + 1) : ''

    let candidateName = filename
    let counter = 0

    // Recursively check if file exists; if it does, increment suffix
    function checkNext() {
      dirEntry.getFile(
        candidateName,
        { create: false }, // do NOT create if doesn't exist
        // If success (file exists), increment suffix
        () => {
          counter++
          const suffix = ` (${counter})`
          candidateName = extension ? `${baseName}${suffix}.${extension}` : `${baseName}${suffix}`
          checkNext()
        },
        // If error is "NOT_FOUND_ERR", file doesn't exist => perfect, use it
        (err) => {
          // On any error (usually 1=NOT_FOUND_ERR), we consider it free
          resolve(candidateName)
        },
      )
    }

    checkNext()
  })
}

export const getStorageKey = (tokenType = 'access') => {
  if (tokenType === 'access') {
    return process.env.FRONTEND_TYPE === 'Internal' ? 'ecd-internal-jwt' : 'ecd-external-jwt'
  } else if (tokenType === 'refresh') {
    return process.env.FRONTEND_TYPE === 'Internal' ? 'ecd-internal-jwt-refresh' : 'ecd-external-jwt-refresh'
  }
  return null
}

export const toIsoHHmm = (dt) =>
  dt.startOf('minute').toISOTime({
    suppressSeconds: true,
    suppressMilliseconds: true,
    includeOffset: false,
  })

export const addClickPreventToLinks = (htmlString) => {
  return htmlString.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
    if (/onclick\s*=\s*["'][^"']*["']/.test(attributes)) {
      return `<a ${attributes}>`
    }
    return `<a ${attributes} onclick="event.preventDefault()">`
  })
}
